import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`So, you've decided to use npm to develop (and maybe publish/deploy)
your project.`}</p>
    <p>{`Fantastic!`}</p>
    <p>{`There are a few things that you need to do above the simple steps
that your users will do to install your program.`}</p>
    <h3>{`About These Documents`}</h3>
    <p>{`These are man pages.  If you install npm, you should be able to
then do `}<inlineCode parentName="p">{`man npm-thing`}</inlineCode>{` to get the documentation on a particular
topic, or `}<inlineCode parentName="p">{`npm help thing`}</inlineCode>{` to see the same information.`}</p>
    <h3>{`What is a Package`}</h3>
    <p>{`A package is:`}</p>
    <ul>
      <li parentName="ul">{`a) a folder containing a program described by a package.json file`}</li>
      <li parentName="ul">{`b) a gzipped tarball containing (a)`}</li>
      <li parentName="ul">{`c) a url that resolves to (b)`}</li>
      <li parentName="ul">{`d) a `}<inlineCode parentName="li">{`<name>@<version>`}</inlineCode>{` that is published on the registry with (c)`}</li>
      <li parentName="ul">{`e) a `}<inlineCode parentName="li">{`<name>@<tag>`}</inlineCode>{` that points to (d)`}</li>
      <li parentName="ul">{`f) a `}<inlineCode parentName="li">{`<name>`}</inlineCode>{` that has a "latest" tag satisfying (e)`}</li>
      <li parentName="ul">{`g) a `}<inlineCode parentName="li">{`git`}</inlineCode>{` url that, when cloned, results in (a).`}</li>
    </ul>
    <p>{`Even if you never publish your package, you can still get a lot of
benefits of using npm if you just want to write a node program (a), and
perhaps if you also want to be able to easily install it elsewhere
after packing it up into a tarball (b).`}</p>
    <p>{`Git urls can be of the form:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git://github.com/user/project.git#commit-ish
git+ssh://user@hostname:project.git#commit-ish
git+http://user@hostname/project/blah.git#commit-ish
git+https://user@hostname/project/blah.git#commit-ish
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`commit-ish`}</inlineCode>{` can be any tag, sha, or branch which can be supplied as
an argument to `}<inlineCode parentName="p">{`git checkout`}</inlineCode>{`.  The default is whatever the repository uses
as its default branch.`}</p>
    <h3>{`The package.json File`}</h3>
    <p>{`You need to have a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file in the root of your project to do
much of anything with npm.  That is basically the whole interface.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/package-json"
      }}><inlineCode parentName="a">{`package.json`}</inlineCode></a>{` for details about what
goes in that file.  At the very least, you need:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`name: This should be a string that identifies your project.  Please do
not use the name to specify that it runs on node, or is in JavaScript.
You can use the "engines" field to explicitly state the versions of node
(or whatever else) that your program requires, and it's pretty well
assumed that it's JavaScript.`}</p>
        <p parentName="li">{`It does not necessarily need to match your github repository name.`}</p>
        <p parentName="li">{`So, `}<inlineCode parentName="p">{`node-foo`}</inlineCode>{` and `}<inlineCode parentName="p">{`bar-js`}</inlineCode>{` are bad names.  `}<inlineCode parentName="p">{`foo`}</inlineCode>{` or `}<inlineCode parentName="p">{`bar`}</inlineCode>{` are better.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`version: A semver-compatible version.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`engines: Specify the versions of node (or whatever else) that your
program runs on.  The node API changes a lot, and there may be bugs or
new functionality that you depend on.  Be explicit.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`author: Take some credit.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`scripts: If you have a special compilation or installation script, then
you should put it in the `}<inlineCode parentName="p">{`scripts`}</inlineCode>{` object.  You should definitely have at
least a basic smoke-test command as the "scripts.test" field.  See
`}<a parentName="p" {...{
            "href": "/cli/v8/using-npm/scripts"
          }}>{`scripts`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`main: If you have a single module that serves as the entry point to your
program (like what the "foo" package gives you at require("foo")), then
you need to specify that in the "main" field.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`directories: This is an object mapping names to folders.  The best ones
to include are "lib" and "doc", but if you use "man" to specify a folder
full of man pages, they'll get installed just like these ones.`}</p>
      </li>
    </ul>
    <p>{`You can use `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` in the root of your package in order to get you
started with a pretty basic package.json file.  See `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-init"
      }}><inlineCode parentName="a">{`npm
init`}</inlineCode></a>{` for more info.`}</p>
    <h3>{`Keeping files `}<em parentName="h3">{`out`}</em>{` of your Package`}</h3>
    <p>{`Use a `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file to keep stuff out of your package.  If there's no
`}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file, but there `}<em parentName="p">{`is`}</em>{` a `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` file, then npm will ignore
the stuff matched by the `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` file.  If you `}<em parentName="p">{`want`}</em>{` to include
something that is excluded by your `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` file, you can create an
empty `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file to override it. Like `}<inlineCode parentName="p">{`git`}</inlineCode>{`, `}<inlineCode parentName="p">{`npm`}</inlineCode>{` looks for
`}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` and `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` files in all subdirectories of your package,
not only the root directory.`}</p>
    <p><inlineCode parentName="p">{`.npmignore`}</inlineCode>{` files follow the `}<a parentName="p" {...{
        "href": "https://git-scm.com/book/en/v2/Git-Basics-Recording-Changes-to-the-Repository#_ignoring"
      }}>{`same pattern
rules`}</a>{`
as `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` files:`}</p>
    <ul>
      <li parentName="ul">{`Blank lines or lines starting with `}<inlineCode parentName="li">{`#`}</inlineCode>{` are ignored.`}</li>
      <li parentName="ul">{`Standard glob patterns work.`}</li>
      <li parentName="ul">{`You can end patterns with a forward slash `}<inlineCode parentName="li">{`/`}</inlineCode>{` to specify a directory.`}</li>
      <li parentName="ul">{`You can negate a pattern by starting it with an exclamation point `}<inlineCode parentName="li">{`!`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`By default, the following paths and files are ignored, so there's no
need to add them to `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` explicitly:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`.*.swp`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`._*`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.DS_Store`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.git`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.gitignore`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.hg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.npmignore`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.npmrc`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.lock-wscript`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.svn`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.wafpickle-*`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`config.gypi`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`CVS`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm-debug.log`}</inlineCode></li>
    </ul>
    <p>{`Additionally, everything in `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` is ignored, except for
bundled dependencies. npm automatically handles this for you, so don't
bother adding `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` to `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{`.`}</p>
    <p>{`The following paths and files are never ignored, so adding them to
`}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` is pointless:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`package.json`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`README`}</inlineCode>{` (and its variants)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`CHANGELOG`}</inlineCode>{` (and its variants)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`LICENSE`}</inlineCode>{` / `}<inlineCode parentName="li">{`LICENCE`}</inlineCode></li>
    </ul>
    <p>{`If, given the structure of your project, you find `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` to be a
maintenance headache, you might instead try populating the `}<inlineCode parentName="p">{`files`}</inlineCode>{`
property of `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`, which is an array of file or directory names
that should be included in your package. Sometimes manually picking
which items to allow is easier to manage than building a block list.`}</p>
    <h4>{`Testing whether your `}<inlineCode parentName="h4">{`.npmignore`}</inlineCode>{` or `}<inlineCode parentName="h4">{`files`}</inlineCode>{` config works`}</h4>
    <p>{`If you want to double check that your package will include only the files
you intend it to when published, you can run the `}<inlineCode parentName="p">{`npm pack`}</inlineCode>{` command locally
which will generate a tarball in the working directory, the same way it
does for publishing.`}</p>
    <h3>{`Link Packages`}</h3>
    <p><inlineCode parentName="p">{`npm link`}</inlineCode>{` is designed to install a development package and see the
changes in real time without having to keep re-installing it.  (You do
need to either re-link or `}<inlineCode parentName="p">{`npm rebuild -g`}</inlineCode>{` to update compiled packages,
of course.)`}</p>
    <p>{`More info at `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-link"
      }}><inlineCode parentName="a">{`npm link`}</inlineCode></a>{`.`}</p>
    <h3>{`Before Publishing: Make Sure Your Package Installs and Works`}</h3>
    <p><strong parentName="p">{`This is important.`}</strong></p>
    <p>{`If you can not install it locally, you'll have
problems trying to publish it.  Or, worse yet, you'll be able to
publish it, but you'll be publishing a broken or pointless package.
So don't do that.`}</p>
    <p>{`In the root of your package, do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install . -g
`}</code></pre>
    <p>{`That'll show you that it's working.  If you'd rather just create a symlink
package that points to your working directory, then do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm link
`}</code></pre>
    <p>{`Use `}<inlineCode parentName="p">{`npm ls -g`}</inlineCode>{` to see if it's there.`}</p>
    <p>{`To test a local install, go into some other folder, and then do:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`cd ../some-other-folder
npm install ../my-package
`}</code></pre>
    <p>{`to install it locally into the node_modules folder in that other place.`}</p>
    <p>{`Then go into the node-repl, and try using require("my-thing") to
bring in your module's main module.`}</p>
    <h3>{`Create a User Account`}</h3>
    <p>{`Create a user with the adduser command.  It works like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm adduser
`}</code></pre>
    <p>{`and then follow the prompts.`}</p>
    <p>{`This is documented better in `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-adduser"
      }}>{`npm adduser`}</a>{`.`}</p>
    <h3>{`Publish your Package`}</h3>
    <p>{`This part's easy.  In the root of your folder, do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm publish
`}</code></pre>
    <p>{`You can give publish a url to a tarball, or a filename of a tarball,
or a path to a folder.`}</p>
    <p>{`Note that pretty much `}<strong parentName="p">{`everything in that folder will be exposed`}</strong>{`
by default.  So, if you have secret stuff in there, use a
`}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file to list out the globs to ignore, or publish
from a fresh checkout.`}</p>
    <h3>{`Brag about it`}</h3>
    <p>{`Send emails, write blogs, blab in IRC.`}</p>
    <p>{`Tell the world how easy it is to install your program!`}</p>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm"
        }}>{`npm`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-init"
        }}>{`npm init`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/package-json"
        }}>{`package.json`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/scripts"
        }}>{`npm scripts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-publish"
        }}>{`npm publish`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-adduser"
        }}>{`npm adduser`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/registry"
        }}>{`npm registry`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      